import { FC, useRef } from "react";
import { v4 as uuid } from "uuid";
import { observer } from "mobx-react";
import {
  ButtonGroup,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  Typography,
} from "@mui/material";

import CoreInput from "../../../core/CoreInput";
import { AttachmentIcon } from "../../../../icons";
import { useAppStore } from "../../../../../hooks/useAppStore";
import useNotifier from "../../../../../hooks/useNotifier";
import CoreTextarea from "../../../core/CoreTextarea";
import {
  CONTACT_SUPPORT_PURPOSE_TYPES,
  CONTACT_SUPPORT_FORM_FIELD_MAX_LENGTH,
  ContactSupportFormType,
  DENIAL_TYPE_OPTIONS,
} from "../../../../../config/constants";
import CoreSelect from "../../../core/CoreSelect";

interface ContactSupportFormContentProps {
  purpose: string;
  setPurpose: (purpose: string) => void;
  formData: ContactSupportFormType;
  setFormData: (formData: ContactSupportFormType) => void;
  isDisabled: boolean;
}

const ContactSupportModalFormContent: FC<ContactSupportFormContentProps> = observer(
  ({ purpose, setPurpose, formData, setFormData, isDisabled }) => {
    const notify = useNotifier();
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const {
      contactSupportUploadFiles: uploadedFiles,
      setContactSupportUploadFiles: setUploadedFiles,
    } = useAppStore();

    const onPurposeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setPurpose(event.target.value);
      // Also clear the form data for the new purpose
      setFormData({ ...formData, surgeryDate: "", claimNumber: "", denialType: "" });
    };

    const onOpenFilePicker = () => {
      fileInputRef.current?.click();
    };

    const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = event.target.files;

      if (!selectedFiles || selectedFiles.length === 0) {
        notify.showWarning("Please select at least one file");
        return;
      }

      setUploadedFiles([
        ...uploadedFiles,
        ...Array.from(selectedFiles).map((file) => ({
          file,
          status: "attached" as const,
          id: uuid(),
        })),
      ]);
      event.target.value = "";
    };

    const updateForm = (field: keyof ContactSupportFormType, value: string) => {
      setFormData({
        ...formData,
        [field]: value,
      });
    };

    return (
      <>
        <FormControl>
          <FormLabel id="demo-row-radio-buttons-group-label">
            Purpose for request:
          </FormLabel>
          <RadioGroup
            row
            aria-labelledby="demo-row-radio-buttons-group-label"
            name="row-radio-buttons-group"
            onChange={onPurposeChange}
            value={purpose}
          >
            {Object.values(CONTACT_SUPPORT_PURPOSE_TYPES).map((puposeValue) => (
              <FormControlLabel
                disabled={isDisabled}
                key={puposeValue}
                value={puposeValue}
                control={<Radio />}
                label={puposeValue}
              />
            ))}
          </RadioGroup>
        </FormControl>

        {!purpose ? null : purpose === CONTACT_SUPPORT_PURPOSE_TYPES.SUPPORT ? (
          <>
            <CoreInput
              disabled={isDisabled}
              label="Subject"
              value={formData.subject}
              maxChars={CONTACT_SUPPORT_FORM_FIELD_MAX_LENGTH.subject}
              onChange={(e) => updateForm("subject", e.target.value)}
            />

            <CoreTextarea
              title="Message"
              disabled={isDisabled}
              value={formData.comment}
              minRows={4}
              maxRows={6}
              disabledSx={{ backgroundColor: "white" }}
              onChange={(e) => updateForm("comment", e.target.value)}
              style={{ resize: "none" }}
              maxChars={CONTACT_SUPPORT_FORM_FIELD_MAX_LENGTH.comment}
            />
          </>
        ) : (
          <>
            <CoreInput
              disabled={isDisabled}
              label="Patient Name"
              value={formData.patientName}
              onChange={(e) => updateForm("patientName", e.target.value)}
              maxChars={CONTACT_SUPPORT_FORM_FIELD_MAX_LENGTH.patientName}
            />
            <CoreInput
              disabled={isDisabled}
              label="MRN"
              value={formData.mrn}
              onChange={(e) => updateForm("mrn", e.target.value)}
              maxChars={CONTACT_SUPPORT_FORM_FIELD_MAX_LENGTH.mrn}
            />
          </>
        )}

        {purpose === CONTACT_SUPPORT_PURPOSE_TYPES.PRESERVICE_REVIEW ? (
          <CoreInput
            disabled={isDisabled}
            label="Surgery Date"
            value={formData.surgeryDate}
            type="date"
            onChange={(e) => updateForm("surgeryDate", e.target.value)}
          />
        ) : null}

        {purpose === CONTACT_SUPPORT_PURPOSE_TYPES.DENIAL_MANAGEMENT ? (
          <>
          <CoreInput
            disabled={isDisabled}
            label="Claim #"
            value={formData.claimNumber}
            onChange={(e) => updateForm("claimNumber", e.target.value)}
            maxChars={CONTACT_SUPPORT_FORM_FIELD_MAX_LENGTH.claimNumber}
          />
          <CoreSelect
              disabled={isDisabled}
              label="Denial Type"
              value={formData.denialType}
              onChange={(e: SelectChangeEvent<unknown>) =>
                updateForm("denialType", e.target.value as string)
              }
              options={DENIAL_TYPE_OPTIONS}
            />
          </>
        ) : null}

        {purpose ? (
          <>
            {purpose !== CONTACT_SUPPORT_PURPOSE_TYPES.SUPPORT ? (
              <CoreTextarea
                title="Message"
                disabled={isDisabled}
                value={formData.comment}
                minRows={4}
                maxRows={6}
                disabledSx={{ backgroundColor: "white" }}
                onChange={(e) => updateForm("comment", e.target.value)}
                style={{ resize: "none" }}
                maxChars={CONTACT_SUPPORT_FORM_FIELD_MAX_LENGTH.comment}
              />
            ) : null}

            <ButtonGroup
              disabled={isDisabled}
              onClick={isDisabled ? undefined : onOpenFilePicker}
              sx={{
                marginTop: "10px",
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: isDisabled ? "not-allowed" : "pointer",
                color: "text.secondary",
                "&:hover": { color: "primary.main" },
                opacity: isDisabled ? 0.5 : 1,
              }}
            >
              <IconButton
                disabled={isDisabled}
                aria-label="Add attachment"
                size="small"
                sx={{
                  borderRadius: "4px",
                  border: "1px solid",
                  borderColor: "divider",
                  width: "32px",
                  height: "32px",
                }}
              >
                <AttachmentIcon />
              </IconButton>
              <Typography
                variant="h5"
                color="inherit"
                fontWeight={500}
                component="span"
                sx={{ whiteSpace: "nowrap" }}
              >
                {uploadedFiles.length > 0
                  ? `${uploadedFiles.length} file(s) attached`
                  : "Drop files here or click to upload documents"}
              </Typography>
            </ButtonGroup>

            <input
              type="file"
              multiple={true}
              ref={fileInputRef}
              onChange={onFileChange}
              className="hidden"
            />
          </>
        ) : null}
      </>
    );
  },
);

export default ContactSupportModalFormContent;
